import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Register from './components/Register';
import ForgetPassword from './components/ForgetPassword';
import Dashboard from './components/Dashboard';
import Sidebar from './components/Sidebar';
import Deliveries from './components/Deliveries';
import Tracking from './components/Tracking';
import TrackByCustomer from './components/TrackByCustomer';
import TrackByBillNo from './components/TrackByBillNo';
import Analytics from './components/Analytics';
import OrderReports from './components/OrderReports';
import CustomerReport from './components/CustomerReport';
import PaymentReport from './components/PaymentReport';
import ReturnReport from './components/ReturnReport';
import VATReport from './components/VATReport';
import Settings from './components/Settings';
import NewOrder from './components/NewOrder';
import UploadOrders from './components/UploadOrders';
import MyCustomers from './components/MyCustomers';
import OrderDetails from './components/OrderDetails';
import BatchPrinting from './components/BatchPrinting';
import { ToastProvider } from './contexts/ToastContext';
import ToastContainer from './components/ToastContainer';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing token on app load
  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
        if (tokenData) {
          const userData = JSON.parse(tokenData);
          if (userData && userData.Token) {
            setIsAuthenticated(true);
            setUser(userData);
          }
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        localStorage.removeItem('AINSOFT_DRIVER_TOKEN');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('AINSOFT_DRIVER_TOKEN');
    setShowLogoutConfirm(false);
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-primary-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <ToastProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forget-password" element={<ForgetPassword />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          <ToastContainer />
        </Router>
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <Router>
        <div className="flex h-screen gradient-bg">
          <Sidebar user={user} onLogout={handleLogout} />
          <div className="flex-1 overflow-auto overflow-x-hidden">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/new-order" element={<NewOrder />} />
              <Route path="/upload-orders" element={<UploadOrders />} />
              <Route path="/deliveries" element={<Deliveries />} />
              <Route path="/tracking" element={<Tracking />} />
              <Route path="/track-by-customer" element={<TrackByCustomer />} />
              <Route path="/track-by-bill" element={<TrackByBillNo />} />
              <Route path="/my-customers" element={<MyCustomers />} />
              <Route path="/order-details/:orderId" element={<OrderDetails />} />
              <Route path="/batch-printing" element={<BatchPrinting />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/order-reports" element={<OrderReports />} />
              <Route path="/customer-report" element={<CustomerReport />} />
              <Route path="/payment-report" element={<PaymentReport />} />
              <Route path="/return-report" element={<ReturnReport />} />
              <Route path="/vat-report" element={<VATReport />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </div>
        <ToastContainer />
        
        {/* Logout Confirmation Modal */}
        {showLogoutConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Confirm Sign Out</h3>
                  <p className="text-sm text-gray-500">Are you sure you want to sign out?</p>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-800">You will be logged out of your account</p>
                    <p className="text-xs text-red-600 mt-1">All unsaved changes will be lost and you'll need to sign in again to access your account.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLogout}
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Yes, Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </Router>
    </ToastProvider>
  );
}

export default App; 